/**
 * Document References Sidebar
 * Multi-Chat System: Shows all documents referenced in the current conversation
 *
 * Features:
 * - Lightweight ListItem-based design for compact, scannable display
 * - Clean, minimal design with reduced color usage
 * - Subtle gray icon backgrounds
 * - Inline action buttons for quick access
 * - Compact message reference chips
 * - Subtle hover effects (background color transition)
 * - Keyboard navigation (Esc to close)
 * - Comprehensive accessibility (ARIA labels, screen reader support)
 * - Mobile responsive design
 */

import React, { useMemo, useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  IconButton,
  Divider,
  Link,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
  Fade,
} from '@mui/material';
import {
  Description as DocumentIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  Link as LinkIcon,
  Business as BusinessIcon,
  Close as CloseIcon,
  Article as ArticleIcon,
  ChatBubbleOutline as ChatBubbleOutlineIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useGetConversation } from '../../hooks/api/useConversations';
import { log } from '../../utils/errors/logger';
import { downloadOriginalDocument, viewOriginalDocument } from '../../utils/documents/documentHelpers';
import { FAQDetailDialog } from '../faqs/FAQDetailDialog';
import { useAuth } from '../../hooks/auth/useAuth';
import { useSnackbar } from '../../contexts/SnackbarContext';

interface DocumentReferencesSidebarProps {
  open: boolean;
  onClose: () => void;
}

interface MessageSource {
  title?: string;
  url: string;
  download_url?: string;
  source_type?: 'sap' | 'custom' | 'faq';
  document_id?: string;
  relevance_score?: number;
}

interface DocumentReference {
  title: string;
  url: string;
  download_url?: string;
  source_type: 'sap' | 'custom' | 'external' | 'faq';
  document_id?: string;
  relevance_score?: number;
  messageIndices: number[];
}

// Constants
const MAX_VISIBLE_MESSAGE_REFS = 5;

export const DocumentReferencesSidebar: React.FC<DocumentReferencesSidebarProps> = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { showError } = useSnackbar();

  // FAQ dialog state
  const [faqDialogOpen, setFaqDialogOpen] = useState(false);
  const [selectedFaqId, setSelectedFaqId] = useState<string | null>(null);

  // Get current conversation ID from URL
  const { conversationId: currentConversationId } = useParams<{ conversationId?: string }>();

  // Get conversation details with messages
  const { data: conversation, isLoading, error } = useGetConversation(currentConversationId || null, {
    enabled: !!currentConversationId,
  });

  // Extract unique document references from all messages
  const documentReferences = useMemo(() => {
    if (!conversation?.messages) return [];

    const referencesMap = new Map<string, DocumentReference>();

    conversation.messages.forEach((message, messageIndex) => {
      if (message.sources && message.sources.length > 0) {
        message.sources.forEach((source: MessageSource) => {
          // Create unique key based on document ID or URL
          const key = source.document_id || source.url;

          if (referencesMap.has(key)) {
            // Add message index to existing reference
            const existing = referencesMap.get(key)!;
            existing.messageIndices.push(messageIndex);
            // Update relevance score if higher
            if (source.relevance_score && (!existing.relevance_score || source.relevance_score > existing.relevance_score)) {
              existing.relevance_score = source.relevance_score;
            }
          } else {
            // Add new reference
            referencesMap.set(key, {
              title: source.title || t('documents.untitled'),
              url: source.url,
              download_url: source.download_url,
              source_type: source.source_type || 'external',
              document_id: source.document_id,
              relevance_score: source.relevance_score,
              messageIndices: [messageIndex],
            });
          }
        });
      }
    });

    return Array.from(referencesMap.values());
  }, [conversation, t]);

  // Component logging
  useEffect(() => {
    if (open && currentConversationId) {
      log.component.mount('DocumentReferencesSidebar', {
        conversationId: currentConversationId,
        referenceCount: documentReferences.length
      });
    }
  }, [open, currentConversationId, documentReferences.length]);

  // Keyboard shortcuts (Esc to close)
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!open) return;

      // Escape - Close sidebar
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    },
    [open, onClose]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  // Event handlers
  const handleDownloadDocument = async (ref: DocumentReference) => {
    try {
      let docId = ref.document_id;

      if (ref.url?.startsWith('/document/view/')) {
        docId = ref.url.replace('/document/view/', '');
      }

      const downloadTarget = ref.download_url || docId;

      if (downloadTarget) {
        await downloadOriginalDocument(downloadTarget);

        log.user.action('document_reference_downloaded', 'DocumentReferencesSidebar', {
          source_type: ref.source_type,
          document_id: ref.document_id ?? null,
          title: ref.title,
        });
      } else {
        log.warn('No download target available for document', {
          source_type: ref.source_type,
          title: ref.title,
        });
      }
    } catch (error) {
      log.error('Error downloading document from references', { error, title: ref.title });
      showError(t('conversation.references.errors.downloadFailed', { title: ref.title }));
    }
  };

  const handleShowDocument = async (ref: DocumentReference) => {
    try {
      if (ref.source_type === 'faq' && ref.document_id) {
        // Open FAQ detail dialog
        setSelectedFaqId(ref.document_id);
        setFaqDialogOpen(true);

        log.user.action('faq_reference_viewed', 'DocumentReferencesSidebar', {
          faq_id: ref.document_id,
          title: ref.title,
        });
      } else if (ref.source_type === 'custom') {
        let docId = ref.document_id;

        if (ref.url?.startsWith('/document/view/')) {
          docId = ref.url.replace('/document/view/', '');
        }

        if (docId) {
          await viewOriginalDocument(docId);

          log.user.action('document_reference_viewed', 'DocumentReferencesSidebar', {
            source_type: ref.source_type,
            document_id: docId,
            title: ref.title,
          });
        }
      }
    } catch (error) {
      log.error('Error viewing document/FAQ from references', { error, title: ref.title });
      showError(t('conversation.references.errors.viewFailed', { title: ref.title }));
    }
  };

  // Helper functions
  const getSourceIcon = (sourceType: string) => {
    switch (sourceType) {
      case 'sap':
        return <BusinessIcon />;
      case 'custom':
        return <DocumentIcon />;
      default:
        return <LinkIcon />;
    }
  };

  const getSourceLabel = (sourceType: string) => {
    switch (sourceType) {
      case 'sap':
        return 'SAP';
      case 'custom':
        return t('documents.custom');
      default:
        return t('documents.external');
    }
  };

  return (
    <>
    <Fade in={open} timeout={250}>
      <Box
        role="complementary"
        aria-label={t('conversation.documentSidebar')}
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.paper',
          boxShadow: 3,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 3,
            py: 2.5,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'background.default',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <ArticleIcon color="primary" sx={{ fontSize: 28 }} />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                }}
              >
                {t('conversation.references.title')}
              </Typography>
            </Box>

            <Tooltip title={`${t('common.close')} (Esc)`} arrow>
              <IconButton
                onClick={onClose}
                aria-label={t('common.close')}
                sx={{
                  width: 40,
                  height: 40,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'rotate(90deg)',
                    bgcolor: 'action.hover',
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Box>

          {!isLoading && !error && documentReferences.length > 0 && (
            <Typography variant="body2" color="text.secondary">
              {t('conversation.documentsFound', { count: documentReferences.length })}
            </Typography>
          )}
        </Box>

        <Divider />

        {/* Content */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            '&::-webkit-scrollbar': {
              width: 8,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.2)',
              borderRadius: 4,
            },
          }}
        >
          {/* Loading State */}
          {isLoading && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4, gap: 2 }}>
              <CircularProgress size={40} />
              <Typography variant="body2" color="text.secondary">
                {t('conversation.references.loading')}
              </Typography>
            </Box>
          )}

          {/* Error State */}
          {error && (
            <Box sx={{ p: 2 }}>
              <Alert severity="error">
                {t('conversation.references.loadingError')}
              </Alert>
            </Box>
          )}

          {/* No Conversation Selected */}
          {!currentConversationId && !isLoading && (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <ArticleIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {t('conversation.noConversationSelected')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('conversation.references.emptyState.description')}
              </Typography>
            </Box>
          )}

          {/* No Documents */}
          {!isLoading && !error && currentConversationId && documentReferences.length === 0 && (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <ArticleIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {t('conversation.noDocumentReferences')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('conversation.references.emptyState.description')}
              </Typography>
            </Box>
          )}

          {/* Document List */}
          {!isLoading && !error && documentReferences.length > 0 && (
            <List sx={{ p: 0 }}>
              {documentReferences.map((ref, index) => (
                <ListItem
                  key={ref.document_id || ref.url || index}
                  divider
                  sx={{
                    py: 1.5,
                    px: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    gap: 1,
                    transition: 'background-color 0.2s ease-in-out',
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  {/* Document Header */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        bgcolor: 'action.hover',
                        color: 'text.secondary',
                        flexShrink: 0,
                      }}
                    >
                      {React.cloneElement(getSourceIcon(ref.source_type), { sx: { fontSize: 20 } })}
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      {/* Title */}
                      <Link
                        component="button"
                        variant="body2"
                        onClick={() => handleShowDocument(ref)}
                        aria-label={t('conversation.references.actions.view')}
                        sx={{
                          textAlign: 'left',
                          textDecoration: 'none',
                          fontWeight: 600,
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'normal',
                          lineHeight: 1.4,
                          mb: 0.5,
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        {ref.title}
                      </Link>

                      {/* Source Type Chip */}
                      <Chip
                        label={getSourceLabel(ref.source_type)}
                        size="small"
                        variant="outlined"
                        sx={{
                          height: 20,
                          fontSize: '0.7rem',
                          borderColor: 'divider',
                          color: 'text.secondary',
                        }}
                      />
                    </Box>

                    {/* Inline Action Buttons */}
                    <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                      {/* View - for custom documents and FAQs */}
                      {(ref.source_type === 'custom' || ref.source_type === 'faq') && ref.document_id && (
                        <Tooltip title={t('conversation.references.actions.view')}>
                          <IconButton
                            size="small"
                            onClick={() => handleShowDocument(ref)}
                            aria-label={t('conversation.references.actions.view')}
                            sx={{
                              transition: 'all 0.2s',
                              '&:hover': {
                                bgcolor: 'primary.main',
                                color: 'primary.contrastText',
                              },
                            }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}

                      {/* Download - only for custom documents */}
                      {(ref.source_type === 'custom' || ref.url?.startsWith('/document/')) && (
                        <Tooltip title={t('conversation.references.actions.download')}>
                          <IconButton
                            size="small"
                            onClick={() => handleDownloadDocument(ref)}
                            aria-label={t('conversation.references.actions.download')}
                            sx={{
                              transition: 'all 0.2s',
                              '&:hover': {
                                bgcolor: 'primary.main',
                                color: 'primary.contrastText',
                              },
                            }}
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>

                  {/* Message References */}
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center', pl: 6.5 }}>
                    <ChatBubbleOutlineIcon sx={{ fontSize: '0.875rem', color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                      {t('conversation.referencedInMessages')}:
                    </Typography>
                    {ref.messageIndices.slice(0, MAX_VISIBLE_MESSAGE_REFS).map((msgIndex) => (
                      <Chip
                        key={msgIndex}
                        label={`#${msgIndex + 1}`}
                        size="small"
                        variant="outlined"
                        aria-label={t('conversation.references.actions.jumpToMessage', { index: msgIndex + 1 })}
                        sx={{
                          height: 18,
                          fontSize: '0.65rem',
                          borderColor: 'divider',
                          color: 'text.secondary',
                          transition: 'all 0.15s ease-in-out',
                          '&:hover': {
                            borderColor: 'primary.main',
                            bgcolor: 'action.hover',
                            color: 'primary.main',
                          },
                        }}
                      />
                    ))}
                    {ref.messageIndices.length > MAX_VISIBLE_MESSAGE_REFS && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                        +{ref.messageIndices.length - MAX_VISIBLE_MESSAGE_REFS}
                      </Typography>
                    )}
                  </Box>
                </ListItem>
              ))}
            </List>
          )}
        </Box>

        {/* Footer with Keyboard Hint */}
        {!isLoading && documentReferences.length > 0 && (
          <>
            <Divider />
            <Box
              sx={{
                px: 3,
                py: 1.5,
                borderTop: 1,
                borderColor: 'divider',
                bgcolor: 'background.default',
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.5,
                }}
              >
                <kbd style={{
                  padding: '2px 6px',
                  borderRadius: 4,
                  border: '1px solid',
                  borderColor: 'currentColor',
                  fontSize: '0.7rem',
                  fontFamily: 'monospace'
                }}>
                  Esc
                </kbd>
                {t('common.close')}
              </Typography>
            </Box>
          </>
        )}
      </Box>
    </Fade>

    {/* FAQ Detail Dialog */}
    {selectedFaqId && (
      <FAQDetailDialog
        open={faqDialogOpen}
        onClose={() => {
          setFaqDialogOpen(false);
          setSelectedFaqId(null);
        }}
        faqId={selectedFaqId}
        isModerator={user?.is_moderator || false}
        currentUserId={user?.user_id || null}
      />
    )}
    </>
  );
};
